/**
 * Taking payment.
 *
 * The one screen in the app with no optimistic updates. Everywhere else a fast
 * local response is worth a rare correction; here a screen that says "paid"
 * before the server agrees is a screen that sends a guest away without taking
 * their money.
 *
 * The outstanding amount is the largest thing on it, because it is what gets
 * read aloud, and it comes from the server on every render.
 *
 * Nothing on this screen opens the on-screen keyboard. Amounts are entered on
 * a keypad: a soft keyboard covers half a tablet, hides the decimal point
 * behind a shift layer, and offers a QWERTY grid to someone typing "20". The
 * value stays a string the whole way to the server — money that has been
 * through a JavaScript float is money the browser has rounded.
 */

import { useMemo, useState } from 'react';
import {
  TENDER_LABELS,
  describeAccountBalance,
  describeAvailableCredit,
  formatGiftCardCode,
  formatMoney,
  useAbandonPayment,
  useBill,
  useCurrentDrawer,
  useEntitlements,
  useGiftCard,
  useHouseAccounts,
  usePaymentAttempt,
  useSplit,
  useSplitParts,
  useStartCard,
  useTakePayment,
  type GiftCard,
  type CardAttempt,
  type HouseAccount,
  trimQty,
  type Order,
  type TenderType,
} from '@restaurant/api-client';
import {
  ActionError,
  Badge,
  Button,
  Empty,
  ErrorNotice,
  Field,
  Input,
  Loading,
  NumberPad,
  Segmented,
  UpgradePrompt,
} from '@restaurant/ui';

import { CardWaiting } from './card-reader';
import { DrawerSheet } from './drawer';
import { kickDrawer } from './printer';
import { ReceiptButton } from './receipt';
import { tillSettled, useThisTill } from './this-till';
import { useTillReader } from './till-reader';

const QUICK_CASH = ['5', '10', '20', '50'];

type Entry = 'amount' | 'tip';

/** The smaller of two decimal strings, returned as it arrived. */
function lesser(a: string, b: string): string {
  return Number(a) <= Number(b) ? a : b;
}

export function PayScreen({
  orderId,
  locationId,
  onSettled,
  onBack,
  collectionNumber = null,
  order,
  name,
}: {
  orderId: string;
  /** The tab, for what is on it. The bill above says only what it comes to. */
  order: Order;
  /** How the tab is said: "Table 4", "Counter 12", "Tab 6". */
  name: string;
  locationId: string | undefined;
  onSettled: () => void;
  onBack: () => void;
  /**
   * The number to call out, on a counter order. Null at a table.
   *
   * Said here because this is the moment the guest is handed their change and
   * walks away from the till, and the last moment anybody can tell them what
   * to listen for.
   */
  collectionNumber?: number | null;
}) {
  const bill = useBill(orderId);
  const takePayment = useTakePayment();
  const abandon = useAbandonPayment();
  const attempt = usePaymentAttempt();

  const entitlements = useEntitlements();
  const till = useThisTill(locationId);
  // The card machine beside this till, if the site has them and one was
  // chosen here. With one, a card goes to the reader and waits for a person to
  // say what it showed; without, it is typed in as before.
  const tillReader = useTillReader(locationId);
  const startCard = useStartCard();
  const [waiting, setWaiting] = useState<CardAttempt | null>(null);
  const [declinedOn, setDeclinedOn] = useState<string | null>(null);
  const [drawerSheet, setDrawerSheet] = useState(false);

  const [tender, setTender] = useState<TenderType>('card');
  const [amount, setAmount] = useState('');
  const [tip, setTip] = useState('');
  const [ways, setWays] = useState('1');
  const [entry, setEntry] = useState<Entry>('amount');
  // How the bill is being divided. Even is the old behaviour and needs nothing
  // from the order; by seat and by item are Professional, and carry the lines
  // through to the payment so nobody is asked to pay for them twice.
  const [how, setHow] = useState<'even' | 'seat' | 'item'>('even');
  const [part, setPart] = useState<string | null>(null);
  // Splitting, a tip and a different amount are each asked for, not laid out.
  // Most bills are paid whole, by one card, with no tip typed: every control
  // on screen for the exception was something a cashier had to read past.
  const [splitting, setSplitting] = useState(false);
  const [tipping, setTipping] = useState(false);
  const [typing, setTyping] = useState(false);

  // What a voucher or an account tender is aimed at. Held here rather than in
  // the panel below so the amount, the button label and the request all agree
  // about which card or which customer is paying — and the card lookup lives
  // here too, so the panel never has to push a result up into its parent
  // while that parent is rendering.
  const [voucherCode, setVoucherCode] = useState<string | null>(null);
  const [account, setAccount] = useState<HouseAccount | null>(null);

  const voucher = useGiftCard(voucherCode ?? undefined);

  // Only asked while cash is the tender: a card-only till never needs to
  // know whether a drawer is open.
  const drawer = useCurrentDrawer(
    tender === 'cash' && tillSettled(till.state) ? locationId : undefined,
    till.terminalId,
  );
  const cashBlocked =
    tender === 'cash' &&
    (till.state.kind === 'unchosen' || (drawer.isSuccess && drawer.data === null));
  const card = voucher.data && spendable(voucher.data) ? voucher.data : null;
  // The keypad is for cash, where what was handed over is typed, and for a
  // different amount or a tip once asked for. Not while cash cannot be taken.
  const keypad = !cashBlocked && (tender === 'cash' || typing || tipping);

  const split = useSplit(orderId, Number(ways));
  const mayDivide = entitlements.has('payments.split_advanced');
  const parts = useSplitParts(orderId, how === 'even' || !mayDivide ? null : how);
  const chosen = parts.data?.parts.find((p) => p.key === part) ?? null;

  const outstanding = bill.data?.outstanding ?? '0.00';

  /**
   * What this tender can actually cover, or null for no limit.
   *
   * Cash and a card have no ceiling worth modelling. A voucher has exactly
   * what is left on it; a house account has whatever is left before its
   * ceiling, and `null` there means no ceiling rather than nothing left.
   */
  const ceiling: string | null =
    tender === 'voucher'
      ? (card?.balance ?? null)
      : tender === 'house_account'
        ? (account?.available_credit ?? null)
        : null;

  const suggested = useMemo(() => {
    // A chosen share wins: the guest is paying for what they had, and the
    // figure on the keypad has to be the one their lines came to.
    const share = chosen
      ? chosen.amount
      : Number(ways) > 1 && split.data?.shares.length
        ? (split.data.shares[0] ?? outstanding)
        : outstanding;
    // A card short of the bill offers what it has rather than being typed in
    // full and refused — the refusal would arrive with a guest waiting and a
    // card in somebody's hand.
    return ceiling === null ? share : lesser(share, ceiling);
  }, [ways, split.data, chosen, outstanding, ceiling]);

  if (bill.isPending) return <Loading label="Loading the bill" />;
  if (bill.isError) return <ErrorNotice message={bill.error.message} />;

  const live = bill.data;

  function afterPayment() {
    setAmount('');
    setTip('');
    setEntry('amount');
    setTipping(false);
    setTyping(false);
    // A card that has just been emptied must not stay selected: the
    // next tender on a part-paid bill is a different instrument.
    setVoucherCode(null);
    setAccount(null);
    // The share that was just paid for is gone from the next answer, so
    // holding onto its key would point at nothing.
    setPart(null);
    // Only now start a fresh attempt — this payment is recorded.
    attempt.reset();
    // Not `onSettled()` here. The bill that came back is already in the
    // cache, so a settled bill re-renders this screen as "Paid in full"
    // — which is where the change due is shown and the receipt is
    // offered. Leaving the moment the bill settled skipped both: a
    // cashier never saw the change they were meant to hand back, and
    // the only screen that knows which order a receipt is for was
    // never drawn. "Done" is what leaves.
  }

  function pay() {
    const value = (amount.trim() || suggested).trim();
    setDeclinedOn(null);
    if (tender === 'card' && tillReader.reader) {
      // Nothing is taken yet: the reader is asked, and a person says what it
      // showed. The amount and the share go with it, so "Approved" records
      // exactly what was keyed in.
      startCard.mutate(
        {
          orderId,
          reader_id: tillReader.reader.id,
          amount: value,
          ...(tip.trim() ? { tip: tip.trim() } : {}),
          ...(till.terminalId ? { terminal_id: till.terminalId } : {}),
          ...(chosen ? { line_ids: chosen.line_ids } : {}),
        },
        {
          onSuccess: setWaiting,
          onError: (error) => {
            if (error.status === 409 && error.body.field === 'amount') {
              void bill.refetch();
              setAmount('');
            }
          },
        },
      );
      return;
    }
    takePayment.mutate(
      {
        orderId,
        tender_type: tender,
        amount: value,
        // Mandatory on these two, and the reason the tenders exist: without
        // them a voucher settled a bill from nowhere and a house account
        // recorded the debt nowhere.
        ...(till.terminalId ? { terminal_id: till.terminalId } : {}),
        ...(tender === 'voucher' && card ? { voucher_code: card.code } : {}),
        ...(tender === 'house_account' && account ? { house_account_id: account.id } : {}),
        // Only when a share was picked. A tender that names no lines paid
        // towards the bill rather than for anything in particular, and
        // inventing an allocation would make the next split disagree with
        // this receipt.
        ...(chosen ? { line_ids: chosen.line_ids } : {}),
        ...(tip.trim() ? { tip: tip.trim() } : {}),
        // Stable across retries. A key regenerated on retry is not an
        // idempotency key, it is a second charge with extra steps.
        idempotencyKey: attempt.key,
      },
      {
        // 409 `field: "amount"` means the bill moved under this screen — a
        // second terminal settling the same table, or a split that landed
        // first. The figure on screen is stale, so fetch it before anybody
        // retries against a number that no longer exists.
        onError: (error) => {
          if (error.status === 409 && error.body.field === 'amount') {
            void bill.refetch();
            setAmount('');
          }
        },
        onSuccess: () => {
          // The drawer opens for cash, if this tablet's printer drives one.
          if (tender === 'cash') void kickDrawer();
          afterPayment();
        },
      },
    );
  }

  if (live.is_settled) {
    return (
      <div className="pay pay--done">
        <div className="pay__settled">
          <p className="pay__settled-head">Paid in full</p>
          {live.change !== '0.00' && (
            <p className="pay__settled-change">{formatMoney(live.change)} change</p>
          )}
          {collectionNumber !== null && (
            <p className="pay__settled-number">
              <span className="pay__settled-number-label">Tell them</span>
              <span className="pay__settled-number-value tabular">{collectionNumber}</span>
            </p>
          )}
          {/* The only moment the till knows which order this is: nothing
              lists a closed tab afterwards, so a receipt not produced here
              cannot be produced from this terminal at all. */}
          <div className="pay__settled-actions">
            <ReceiptButton orderId={orderId} locationId={locationId} />
            <Button variant="primary" size="lg" onClick={onSettled}>
              Done
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Back to the tab. A bill part-paid holds its tab for payment, and going
  // back is the cashier saying "not yet", so the tab is released on the way:
  // this used to be a separate "Cancel payment" beside the back button, and
  // the two read as the same thing. Releasing a tab that was never held does
  // nothing.
  function back() {
    abandon.mutate({ orderId }, { onSuccess: onBack });
  }

  // The same dish at the same price is one line with its count, as it is
  // read back to the guest: "two croquettes", not "croquettes, croquettes".
  const dishes: { key: string; qty: number; name: string; total: number }[] = [];
  for (const line of order.lines) {
    if (line.state === 'voided') continue;
    const each = Number(line.line_total) / Number(line.qty);
    const twin = dishes.find((d) => d.name === line.name && d.total / d.qty === each);
    if (twin) {
      twin.qty += Number(line.qty);
      twin.total += Number(line.line_total);
    } else {
      dishes.push({
        key: line.id,
        qty: Number(line.qty),
        name: line.name,
        total: Number(line.line_total),
      });
    }
  }

  return (
    <div className="pay">
      {/* What is being paid for, so the bill can be read back to the guest. */}
      <section className="pay__bill">
        <header className="pay__bill-head">
          <Button variant="ghost" onClick={back} loading={abandon.isPending}>
            ← {name}
          </Button>
        </header>

        <ul className="pay__lines">
          {dishes.map((dish) => (
            <li key={dish.key}>
              <span className="pay__line-qty tabular">{trimQty(String(dish.qty))}×</span>
              <span className="pay__line-name">{dish.name}</span>
              {/* Display only: what is charged is the server's total below. */}
              <span className="pay__line-sum tabular">
                {formatMoney(dish.total.toFixed(2))}
              </span>
            </li>
          ))}
        </ul>

        <dl className="pay__figures">
          <dt>Total</dt>
          <dd className="tabular">{formatMoney(live.total_due)}</dd>
          {live.paid !== '0.00' && (
            <>
              <dt>Paid</dt>
              <dd className="tabular">{formatMoney(live.paid)}</dd>
            </>
          )}
          {live.tips !== '0.00' && (
            <>
              <dt>Tips</dt>
              <dd className="tabular">{formatMoney(live.tips)}</dd>
            </>
          )}
          {live.change !== '0.00' && (
            <>
              <dt className="pay__change">Change due</dt>
              <dd className="pay__change tabular">{formatMoney(live.change)}</dd>
            </>
          )}
        </dl>

        {live.payments.length > 0 && (
          <ul className="pay__taken">
            {live.payments.map((payment) => (
              <li key={payment.id}>
                <Badge tone={payment.tender_type === 'cash' ? 'neutral' : 'accent'}>
                  {TENDER_LABELS[payment.tender_type] ?? payment.tender_type}
                </Badge>
                <span className="tabular">{formatMoney(payment.amount)}</span>
                {/* What the guest handed over, where it differs from what came
                    off the bill: a £30 note against £25 is £30 given and £5
                    back, and the £25 alone is not the figure they remember. */}
                {Number(payment.tendered) > Number(payment.amount) && (
                  <span className="pay__tendered tabular">
                    {formatMoney(payment.tendered)} given
                  </span>
                )}
                {payment.tip_amount !== '0.00' && (
                  <span className="pay__tip tabular">
                    +{formatMoney(payment.tip_amount)} tip
                  </span>
                )}
                {payment.refunded_amount !== '0.00' && (
                  <span className="pay__refunded tabular">
                    {formatMoney(payment.refunded_amount)} refunded
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}

        {/* Splitting is a question about the bill — who had what — so it is
            asked beside the bill, and the keypad's side keeps its room. */}
        {splitting && (
          <div className="pay__split">
            <div className="pay__split-head">
              <span className="pay__split-label">Split the bill</span>
              <button
                type="button"
                className="pay__more"
                onClick={() => {
                  setSplitting(false);
                  setHow('even');
                  setWays('1');
                  setPart(null);
                  setAmount('');
                }}
              >
                Don’t split
              </button>
            </div>
            {/* Three ways of dividing a bill, and they are different
                questions. Evenly asks how many people; by seat and by item ask
                what each of them had. Offered whatever the plan — the upgrade
                is said when it is picked, not hidden so nobody learns the till
                can do it. */}
            <Segmented
              label="How to split the bill"
              value={how}
              onChange={(value) => {
                setHow(value as 'even' | 'seat' | 'item');
                setPart(null);
                setAmount('');
                setWays(value === 'even' ? '2' : '1');
              }}
              options={[
                { value: 'even', label: 'Evenly' },
                { value: 'seat', label: 'By seat' },
                { value: 'item', label: 'By item' },
              ]}
            />

            {/* How many people, as a count rather than a row of choices: a
                table of six is not "4 ways" and a row of every number is a
                row nobody reads. What each pays is the large figure. */}
            {how === 'even' && (
              <div className="pay__ways">
                <button
                  type="button"
                  className="pay__step"
                  disabled={Number(ways) <= 2}
                  onClick={() => setWays(String(Math.max(2, Number(ways) - 1)))}
                  aria-label="One person fewer"
                >
                  −
                </button>
                <span className="pay__ways-count tabular">{ways} people</span>
                <button
                  type="button"
                  className="pay__step"
                  disabled={Number(ways) >= 20}
                  onClick={() => setWays(String(Math.min(20, Number(ways) + 1)))}
                  aria-label="One person more"
                >
                  +
                </button>
                {split.data && (
                  <span className="pay__share tabular">
                    {formatMoney(split.data.shares[0] ?? '0')} each
                  </span>
                )}
              </div>
            )}

            {how !== 'even' && !mayDivide && (
              <p className="pay__upsell">
                Splitting by seat or by item is on Professional. An even split and a typed
                amount work on this plan.
              </p>
            )}

            {how !== 'even' && mayDivide && parts.isPending && (
              <p className="pay__upsell">Working out the shares…</p>
            )}

            {how !== 'even' && mayDivide && parts.data && parts.data.parts.length === 0 && (
              <p className="pay__upsell">Every line on this bill has been paid for.</p>
            )}

            {/* One tap fills the keypad and carries the lines with it. The
                list is what is left: a share somebody has already settled is
                not here to be charged again. */}
            {how !== 'even' && mayDivide && parts.data && parts.data.parts.length > 0 && (
              <ul className="pay__parts">
                {parts.data.parts.map((each) => (
                  <li key={each.key}>
                    <button
                      type="button"
                      className={`pay__part${part === each.key ? ' is-on' : ''}`}
                      aria-pressed={part === each.key}
                      onClick={() => {
                        setPart(part === each.key ? null : each.key);
                        setAmount('');
                      }}
                    >
                      <span className="pay__part-what">{each.label}</span>
                      <span className="pay__part-sum tabular">{formatMoney(each.amount)}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </section>

      <section className="pay__take">
        {/* The number that gets read out loud, and the largest thing here. */}
        <div className="pay__outstanding">
          <span className="pay__outstanding-label">To pay</span>
          <strong className="pay__outstanding-value tabular">
            {formatMoney(live.outstanding)}
          </strong>
        </div>

        {/* How they are paying: the one question on every bill, so four big
            buttons rather than a strip of small ones. All four always offered —
            a plan without gift cards is a price said when it is picked, not a
            button hidden so nobody learns the till can take them. */}
        <div className="pay__tenders" role="group" aria-label="How they are paying">
          {(['card', 'cash', 'voucher', 'house_account'] as const).map((t) => (
            <button
              key={t}
              type="button"
              className={`pay__tender-btn${tender === t ? ' is-on' : ''}`}
              aria-pressed={tender === t}
              onClick={() => {
                setTender(t);
                setAmount('');
                setVoucherCode(null);
                setAccount(null);
              }}
            >
              {TENDER_LABELS[t]}
            </button>
          ))}
        </div>

        {cashBlocked && (
          <div className="pay__tender">
            <p className="pay__tender-fact">
              {till.state.kind === 'unchosen'
                ? 'This tablet does not know which till it is yet'
                : 'No drawer is open at this till'}
            </p>

            <p className="pay__tender-note">
              Cash needs an open drawer to go into. Card, gift card and on account still work.
            </p>

            <Button size="lg" block onClick={() => setDrawerSheet(true)}>
              {till.state.kind === 'unchosen' ? 'Say which till this is' : 'Open the drawer'}
            </Button>
          </div>
        )}

        {tender === 'voucher' &&
          (entitlements.has('payments.gift_cards') ? (
            <VoucherPanel
              query={voucher}
              card={card}
              outstanding={outstanding}
              onCheck={setVoucherCode}
            />
          ) : (
            <UpgradePrompt
              action="Taking payment by gift card"
              currentPlan={entitlements.plan}
              requiredPlan="professional"
              compact
            />
          ))}

        {tender === 'house_account' &&
          (entitlements.has('payments.house_accounts') ? (
            <AccountPanel account={account} outstanding={outstanding} onPick={setAccount} />
          ) : (
            <UpgradePrompt
              action="Putting a bill on a house account"
              currentPlan={entitlements.plan}
              requiredPlan="professional"
              compact
            />
          ))}

        {/* Two amounts, one pad. Tapping a figure is what moves the keys to it,
            so there is never a hidden focus to guess at. Shown for cash, where
            what was handed over is typed, and once a different amount or a tip
            is asked for. */}
        {keypad && (
          <div className="pay__entries">
            <button
              type="button"
              className={`pay__entry${entry === 'amount' ? ' is-on' : ''}`}
              aria-pressed={entry === 'amount'}
              onClick={() => setEntry('amount')}
            >
              <span className="pay__entry-label">Amount</span>
              <span className="pay__entry-value tabular">
                {amount === '' ? (
                  <span className="pay__entry-ghost">{formatMoney(suggested)}</span>
                ) : (
                  formatMoney(amount)
                )}
              </span>
            </button>
            {tipping && (
              <button
                type="button"
                className={`pay__entry${entry === 'tip' ? ' is-on' : ''}`}
                aria-pressed={entry === 'tip'}
                onClick={() => setEntry('tip')}
              >
                <span className="pay__entry-label">Tip</span>
                <span className="pay__entry-value tabular">
                  {tip === '' ? <span className="pay__entry-ghost">—</span> : formatMoney(tip)}
                </span>
              </button>
            )}
          </div>
        )}

        {tender === 'cash' && keypad && (
          <div className="pay__quick">
            {QUICK_CASH.map((note) => (
              <button
                key={note}
                type="button"
                className="pay__note tabular"
                onClick={() => {
                  setEntry('amount');
                  setAmount(note);
                }}
              >
                {formatMoney(note)}
              </button>
            ))}
            <button
              type="button"
              className="pay__note"
              onClick={() => {
                setEntry('amount');
                setAmount('');
              }}
            >
              Exact
            </button>
          </div>
        )}

        {keypad && (
          <NumberPad
            label={entry === 'amount' ? 'Amount' : 'Tip'}
            value={entry === 'amount' ? amount : tip}
            onChange={entry === 'amount' ? setAmount : setTip}
          />
        )}

        <div className="pay__actions">
          {/* A voucher with no card and an account tender with no customer
              are both a 409 waiting to happen, so the button will not fire
              until the tender has something behind it. */}
          <Button
            variant="primary"
            size="lg"
            block
            disabled={
              cashBlocked ||
              (tender === 'voucher' && card === null) ||
              (tender === 'house_account' && account === null) ||
              Number(amount.trim() || suggested) <= 0
            }
            loading={takePayment.isPending || startCard.isPending}
            onClick={pay}
          >
            Take {formatMoney(amount.trim() || suggested)}
            {tip.trim() !== '' && ` + ${formatMoney(tip)} tip`}
          </Button>
          {tender === 'card' && tillReader.reader && (
            <p className="pay__reader">On {tillReader.reader.name}</p>
          )}
          {declinedOn && (
            <p className="pay__declined" role="status">
              Declined on {declinedOn}. Nothing was taken — try the card again or another way.
            </p>
          )}
          <ActionError error={startCard.error} action="Asking the card reader" />
        </div>
        {waiting && (
          <CardWaiting
            attempt={waiting}
            onPaid={() => {
              setWaiting(null);
              afterPayment();
            }}
            onClosed={(declined) => {
              if (declined) setDeclinedOn(waiting.reader_name);
              setWaiting(null);
            }}
          />
        )}

        <div className="pay__mores">
          {!splitting && (
            <button
              type="button"
              className="pay__more pay__more--btn"
              onClick={() => {
                setSplitting(true);
                // Asked for because there is more than one of them.
                setWays('2');
                setAmount('');
              }}
            >
              Split bill
            </button>
          )}
          {tender !== 'cash' && !typing && (
            <button
              type="button"
              className="pay__more pay__more--btn"
              onClick={() => {
                setTyping(true);
                setEntry('amount');
              }}
            >
              Other amount
            </button>
          )}
          {!tipping && (
            <button
              type="button"
              className="pay__more pay__more--btn"
              onClick={() => {
                setTipping(true);
                setEntry('tip');
              }}
            >
              Add tip
            </button>
          )}
        </div>

        {/* A voucher on a plan without gift cards is a price, not a refusal:
            the server now says which feature and which plan, so this says so
            too rather than printing "forbidden" at somebody mid-transaction. */}
        <ActionError
          error={takePayment.error}
          action="This way of paying"
          title="Payment not taken"
        />
      </section>
      {drawerSheet && (
        <DrawerSheet locationId={locationId} onDismiss={() => setDrawerSheet(false)} />
      )}
    </div>
  );
}

/**
 * Can this card pay for anything at all?
 *
 * Four separate reasons it cannot, and the till has to be able to say which:
 * a stopped card is a conversation with the office, an empty one is a card the
 * guest already spent, and an expired one is a date to read out.
 */
function spendable(card: GiftCard): boolean {
  if (!card.is_active) return false;
  if (Number(card.balance) <= 0) return false;
  return card.expires_at === null || new Date(card.expires_at) >= new Date();
}

/**
 * A voucher, checked before it is spent.
 *
 * The balance is read first because the alternative is worse in exactly the
 * situation this happens in: a card short of the bill, typed in full, refused
 * at the till, with a guest watching. Checking first turns that into an offer —
 * this card covers £30 of the £48, put the rest on something else.
 *
 * Nothing here is cached. Two tills presented with the same card must not both
 * see forty pounds on it, and `useGiftCard` is written never to answer from a
 * cache for that reason.
 */
function VoucherPanel({
  query,
  card,
  outstanding,
  onCheck,
}: {
  query: ReturnType<typeof useGiftCard>;
  card: GiftCard | null;
  outstanding: string;
  onCheck: (code: string | null) => void;
}) {
  const [typed, setTyped] = useState('');

  if (card) {
    const short = Number(card.balance) < Number(outstanding);
    return (
      <div className="pay__tender">
        <p className="pay__tender-head">
          <span className="pay__tender-code">{formatGiftCardCode(card.code)}</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onCheck(null);
              setTyped('');
            }}
          >
            Different card
          </Button>
        </p>
        <p className="pay__tender-fact tabular">{formatMoney(card.balance)} on this card</p>
        {short && (
          <p className="pay__tender-note">
            It covers {formatMoney(card.balance)} of the {formatMoney(outstanding)}. The rest
            needs another tender after this one.
          </p>
        )}
      </div>
    );
  }

  // Present but unusable: found, and refused for a reason worth saying out
  // loud rather than a generic "card declined".
  const refused = query.data ?? null;

  return (
    <form
      className="pay__tender"
      onSubmit={(event) => {
        event.preventDefault();
        const code = typed.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (code) onCheck(code);
      }}
    >
      <Field label="Card code" hint="Dashes and capitals do not matter.">
        <Input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="8HT4-K2QN-9RXD-M6VC"
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
      <Button type="submit" size="lg" block loading={query.isFetching} disabled={typed === ''}>
        Check the card
      </Button>

      {query.isError && (
        <ErrorNotice
          title="That card was not found"
          message="Check it against the card. Codes never contain O, 0, I, 1, S or 5."
        />
      )}
      {refused !== null && (
        <ErrorNotice
          title="This card cannot be used"
          message={
            !refused.is_active
              ? 'It was stopped. Whoever holds it should be sent to the office.'
              : Number(refused.balance) <= 0
                ? 'There is nothing left on it.'
                : `It expired on ${new Date(refused.expires_at as string).toLocaleDateString()}.`
          }
        />
      )}
    </form>
  );
}

/**
 * Putting the bill on somebody's account.
 *
 * A picker rather than a typed code, because an account is a named customer
 * and picking the wrong one bills the wrong company. The ceiling is shown
 * beside each name for the same reason the voucher balance is: an account
 * £40 from its limit should offer to take £40, not fail at £48.
 */
function AccountPanel({
  account,
  outstanding,
  onPick,
}: {
  account: HouseAccount | null;
  outstanding: string;
  onPick: (account: HouseAccount | null) => void;
}) {
  const accounts = useHouseAccounts();

  if (account) {
    const short =
      account.available_credit !== null &&
      Number(account.available_credit) < Number(outstanding);
    return (
      <div className="pay__tender">
        <p className="pay__tender-head">
          <span className="pay__tender-code">{account.name}</span>
          <Button variant="ghost" size="sm" onClick={() => onPick(null)}>
            Different account
          </Button>
        </p>
        <p className="pay__tender-fact">
          {describeAccountBalance(account.balance)} · {describeAvailableCredit(account)}
        </p>
        {short && (
          <p className="pay__tender-note">
            {formatMoney(account.available_credit as string)} of the {formatMoney(outstanding)}{' '}
            fits under their limit. The rest needs another tender.
          </p>
        )}
      </div>
    );
  }

  if (accounts.isPending) return <Loading label="Loading the accounts" />;
  if (accounts.isError) return <ErrorNotice message={accounts.error.message} />;
  if (accounts.data.length === 0) {
    return (
      <Empty
        title="No house accounts yet"
        description="Accounts are opened in the back office, under Credit."
      />
    );
  }

  return (
    <div className="pay__accounts">
      {accounts.data.map((row) => (
        <button
          key={row.id}
          type="button"
          className="pay__account"
          // An account already at its ceiling can take nothing, so it is
          // shown and refused rather than hidden — the waiter has to be able
          // to tell the customer why.
          disabled={row.available_credit !== null && Number(row.available_credit) <= 0}
          onClick={() => onPick(row)}
        >
          <span className="pay__account-name">{row.name}</span>
          <span className="pay__account-meta">
            {describeAccountBalance(row.balance)} · {describeAvailableCredit(row)}
          </span>
        </button>
      ))}
    </div>
  );
}
